import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import type { MapViewData } from "@/lib/aiWidgetMapParser"
import type { RootState } from "./store"

type ChatMapHistory = { maps: MapViewData[]; activeIndex: number }
type AiWidgetMapState = Record<string, ChatMapHistory>

const initialState: AiWidgetMapState = {}

const tsOf = (m: MapViewData) => Date.parse(m.timestamp)

// Insert newMap into maps preserving chronological order by timestamp.
// If a map with the same messageId already exists, no insert happens.
// Returns the final index of the map (existing or freshly inserted).
const insertSorted = (maps: MapViewData[], newMap: MapViewData): number => {
  const existing = maps.findIndex((m) => m.messageId === newMap.messageId)
  if (existing !== -1) return existing
  const newTs = tsOf(newMap)
  let i = 0
  while (i < maps.length && tsOf(maps[i]) <= newTs) i++
  maps.splice(i, 0, newMap)
  return i
}

const aiWidgetMapSlice = createSlice({
  name: "aiWidgetMap",
  initialState,
  reducers: {
    // Live insertion from incoming XMPP stanzas. Chronological sort-insert
    // (handles late MAM backfill). activeIndex follows to the newest map
    // only when the user was already on it (smart auto-jump); otherwise
    // we slide activeIndex to keep pointing at the same map.
    pushAiWidgetMap: (state, action: PayloadAction<MapViewData>) => {
      const { chatJid } = action.payload
      if (!state[chatJid]) state[chatJid] = { maps: [], activeIndex: 0 }
      const entry = state[chatJid]
      const isDuplicate = entry.maps.some(
        (m) => m.messageId === action.payload.messageId,
      )
      const wasOnLatest =
        entry.maps.length === 0 || entry.activeIndex === entry.maps.length - 1

      const insertedIdx = insertSorted(entry.maps, action.payload)

      // Shift activeIndex if an older map was inserted before the one the
      // user is currently viewing, so they keep seeing the same map.
      if (!isDuplicate && insertedIdx <= entry.activeIndex && !wasOnLatest) {
        entry.activeIndex += 1
      }

      if (wasOnLatest) {
        entry.activeIndex = entry.maps.length - 1
      }
    },

    // Explicit user action: clicked a message → jump to that map.
    // Inserts the map at its chronological position if not present,
    // then sets activeIndex to the map's position.
    selectAiWidgetMap: (state, action: PayloadAction<MapViewData>) => {
      const { chatJid } = action.payload
      if (!state[chatJid]) state[chatJid] = { maps: [], activeIndex: 0 }
      const entry = state[chatJid]
      const insertedIdx = insertSorted(entry.maps, action.payload)
      entry.activeIndex = insertedIdx
    },

    setAiWidgetMapIndex: (
      state,
      action: PayloadAction<{ chatJid: string; index: number }>,
    ) => {
      if (state[action.payload.chatJid]) {
        const clamped = Math.max(
          0,
          Math.min(
            action.payload.index,
            state[action.payload.chatJid].maps.length - 1,
          ),
        )
        state[action.payload.chatJid].activeIndex = clamped
      }
    },

    // Removes all maps for a chat — the widget panel hides itself
    // (Root.tsx watches selectAiWidgetMapCount). The Message.aiWidgetMap
    // field on each message stays intact, so the user can re-open any
    // map by clicking "Show map" again.
    clearAiWidgetMaps: (state, action: PayloadAction<{ chatJid: string }>) => {
      delete state[action.payload.chatJid]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const {
  pushAiWidgetMap,
  selectAiWidgetMap,
  setAiWidgetMapIndex,
  clearAiWidgetMaps,
} = aiWidgetMapSlice.actions

export const selectActiveAiWidgetMap =
  (chatJid: string) =>
  (state: RootState): MapViewData | undefined => {
    const history = state.aiWidgetMap[chatJid]
    if (!history) return undefined
    return history.maps[history.activeIndex]
  }

export const selectAiWidgetMapCount =
  (chatJid: string) =>
  (state: RootState): number =>
    state.aiWidgetMap[chatJid]?.maps.length ?? 0

export const selectAiWidgetMapIndex =
  (chatJid: string) =>
  (state: RootState): number =>
    state.aiWidgetMap[chatJid]?.activeIndex ?? 0

// Returns the position of a given messageId in this chat's map list, or -1.
export const selectAiWidgetMapMessageIndex =
  (chatJid: string, messageId: string) =>
  (state: RootState): number =>
    state.aiWidgetMap[chatJid]?.maps.findIndex(
      (m) => m.messageId === messageId,
    ) ?? -1

export default aiWidgetMapSlice.reducer
